import { type ReactNode, useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import type { OfferFlow } from '@/lib/analytics-events';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { DealState } from '@/lib/graphql/generated/graphql';
import {
  registerTabSuperProperties,
  unregisterTabSuperProperties,
} from '@/lib/mixpanel';
import { Loader2 } from 'lucide-react';

export type FlowGuardPage = 'entry' | 'legacy' | 'start' | 'csv';

export type FlowGuardInput = {
  page: FlowGuardPage;
  isInternalUser: boolean | undefined;
  isOfferControlFlow: boolean;
  closed: boolean;
};

export type FlowGuardDecision =
  | { kind: 'render' }
  | { kind: 'wait' }
  | { kind: 'redirect'; to: '' | 'legacy' | 'csv' | 'start' };

export function resolveFlowGuard(input: FlowGuardInput): FlowGuardDecision {
  if (input.isInternalUser === undefined) {
    return { kind: 'wait' };
  }
  if (!input.isInternalUser) {
    return input.page === 'entry'
      ? { kind: 'render' }
      : { kind: 'redirect', to: '' };
  }
  if (input.page === 'entry') {
    // A temporarily empty CSV offer must first go through its own resolver.
    return {
      kind: 'redirect',
      to: input.isOfferControlFlow ? 'csv' : input.closed ? 'legacy' : 'start',
    };
  }
  if (input.page === 'csv' && !input.isOfferControlFlow) {
    return { kind: 'redirect', to: 'start' };
  }
  return { kind: 'render' };
}

const OFFER_FLOW_OF_PAGE: Record<FlowGuardPage, OfferFlow | 'entry'> = {
  entry: 'so_negotiation',
  legacy: 'so_negotiation',
  start: 'entry',
  csv: 'csv_control',
};

function useOfferFlowSuperProperties(
  offerFlow: OfferFlow | 'entry' | null,
  isOfferControlFlow: boolean
) {
  useLayoutEffect(() => {
    if (!offerFlow) return;
    registerTabSuperProperties({ offerFlow, isOfferControlFlow });
    return () =>
      unregisterTabSuperProperties(['offerFlow', 'isOfferControlFlow']);
  }, [offerFlow, isOfferControlFlow]);
}

export function FlowGuard({
  page,
  children,
}: {
  page: FlowGuardPage;
  children: ReactNode;
}) {
  const { deal } = useDealContext();
  const user = useUser();
  const { search, hash, state } = useLocation();
  const decision = deal
    ? resolveFlowGuard({
        page,
        isInternalUser: user ? user.isInternalUser === true : undefined,
        isOfferControlFlow: deal.isOfferControlFlow,
        closed: deal.state === DealState.NegotiationClosed,
      })
    : { kind: 'wait' as const };
  useOfferFlowSuperProperties(
    decision.kind === 'render' ? OFFER_FLOW_OF_PAGE[page] : null,
    deal?.isOfferControlFlow ?? false
  );

  if (decision.kind === 'wait') {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (decision.kind === 'redirect') {
    const suffix = decision.to ? `/${decision.to}` : '';
    return (
      <Navigate
        to={`/deal/${deal.id}${suffix}${search}${hash}`}
        state={state}
        replace
      />
    );
  }
  return <>{children}</>;
}
